import { useCallback, useState, type FormEvent } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import { clientService } from '../../services/clients';
import { migrationService, type ScopeView } from '../../services/migration';
import { EmptyState, ErrorState, LoadMoreButton, LoadingSpinner, Modal } from '../../components/common';
import { describeSelector, selectorFromForm, STAGE_LABELS } from './logic';

/** The migration scopes: each holds (or releases) delivery for the devices it covers. */
export function ScopesPanel({
  selectedId,
  onSelect,
  reloadKey,
}: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  reloadKey: number;
}) {
  const { tenantId } = useTenant();
  const [creating, setCreating] = useState(false);
  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) => migrationService.listScopes(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<ScopeView>(fetchPage, `scopes-${reloadKey}`);

  return (
    <section className="card" aria-label="Migration scopes">
      <div className="section-heading-row">
        <h2>Scopes</h2>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>New scope</button>
      </div>
      <p className="text-muted">
        A scope is a set of devices migrated together. Creating one changes nothing: it starts at <strong>Connect</strong>.
      </p>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState title="No scopes yet" description="Create a scope to begin migrating." />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Covers</th><th>Stage</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {list.items.map((s) => (
                <tr key={s.id} aria-selected={selectedId === s.id}>
                  <td>{s.name}</td>
                  <td>{describeSelector(s.selector as Record<string, unknown>)}</td>
                  <td>
                    <span className={`status-badge ${s.stage === 'live' ? 'status-active' : s.stage === 'connect' ? 'status-disabled' : 'status-pending'}`}>
                      {STAGE_LABELS[s.stage]}
                    </span>
                  </td>
                  <td className="row-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => onSelect(selectedId === s.id ? null : s.id)}>
                      {selectedId === s.id ? 'Close' : 'Open'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton hasMore={list.hasMore} loadingMore={list.loadingMore} loadMoreError={list.loadMoreError} onLoadMore={() => void list.loadMore()} />
      {creating && tenantId && (
        <NewScopeModal
          tenantId={tenantId}
          onClose={() => setCreating(false)}
          onCreated={(s) => { setCreating(false); void list.reload(); onSelect(s.id); }}
        />
      )}
    </section>
  );
}

function NewScopeModal({ tenantId, onClose, onCreated }: { tenantId: string; onClose: () => void; onCreated: (s: ScopeView) => void }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'all' | 'clients' | 'groups'>('clients');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [groupsText, setGroupsText] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const fetchClients = useCallback(
    (tid: string, startAfter: string | undefined) => clientService.list(tid, { limit: 100, startAfter }),
    []
  );
  const clients = useCursorList<{ id: string; name: string }>(fetchClients as never, 'clients');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const selector = selectorFromForm({ mode, clientIds, groupsText });
      onCreated(await migrationService.createScope(tenantId, { name: name.trim(), selector }));
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal title="New migration scope" onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="scope-name">Name</label>
          <input id="scope-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <fieldset className="form-field">
          <legend>Which devices does it cover?</legend>
          {(['clients', 'groups', 'all'] as const).map((m) => (
            <label key={m} className="checkbox-label">
              <input type="radio" name="scope-mode" checked={mode === m} onChange={() => setMode(m)} />
              {m === 'clients' ? 'Specific clients' : m === 'groups' ? 'Resource groups' : 'The whole tenant'}
            </label>
          ))}
        </fieldset>
        {mode === 'clients' && (
          <div className="form-field">
            {clients.error ? <ErrorState error={clients.error} onRetry={() => void clients.reload()} /> : clients.loading ? <LoadingSpinner /> : clients.items.length === 0 ? <p className="text-muted">No clients yet.</p> : (
              <>
              {clients.items.map((c) => (
                <label key={c.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={clientIds.includes(c.id)}
                    onChange={(e) => setClientIds(e.target.checked ? [...clientIds, c.id] : clientIds.filter((x) => x !== c.id))}
                  />
                  {c.name}
                </label>
              ))}
              <LoadMoreButton hasMore={clients.hasMore} loadingMore={clients.loadingMore} loadMoreError={clients.loadMoreError} onLoadMore={() => void clients.loadMore()} />
              </>
            )}
          </div>
        )}
        {mode === 'groups' && (
          <div className="form-field">
            <label htmlFor="scope-groups">Resource groups (comma separated)</label>
            <input id="scope-groups" value={groupsText} onChange={(e) => setGroupsText(e.target.value)} />
          </div>
        )}
        {mode === 'all' && <p className="notice">“The whole tenant” will silence every device once a scope using it is imported. Prefer a narrower scope.</p>}
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>Create scope</button>
        </div>
      </form>
    </Modal>
  );
}
